import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Paperclip, Trash2 } from 'lucide-react';
import { updateProforma, getProforma } from '../../../../services/finance_services/proformaService';
import { getProformaAttachments, uploadProformaAttachment, deleteProformaAttachment } from '../../../../services/finance_attachment_services/proformaAttachmentService';
import useApi from '../../../../hooks/useApi';
import Message from '../../../../components/Message';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
  { value: 'converted', label: 'Converted to Invoice' }
];

const emptyItem = { description: '', quantity: 1, unit_price: 0, tax_rate: 0, discount_amount: 0 };

const EditInviterProformaPage = () => {
  const { invitationId, id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    proforma_number: '',
    title: '',
    client: '',
    client_address: '',
    client_email: '',
    client_phone: '',
    date_issued: '',
    expiry_date: '',
    status: 'draft',
    terms_and_conditions: '',
    notes: '',
    items: [{ ...emptyItem }]
  });

  const [files, setFiles] = useState([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [deletingAttachments, setDeletingAttachments] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState(null);

  // Use useApi for update operation
  const { 
    loading, 
    error, 
    callApi: saveProforma,
    setError,
    setSuccessMessage
  } = useApi((id, data) => updateProforma(id, data, invitationId), {showSuccessToast: true, msg: 'Proforma updated'});

  useEffect(() => {
    const loadData = async () => {
      try {
        setInitialLoading(true);
        setInitialError(null);
        console.log('Loading proforma with ID:', id); // Debug log
        
        // Call the service directly without useApi for debugging
        const proformaResponse = await getProforma(id, invitationId);
        console.log('Direct proforma response:', proformaResponse); // Debug log
        
        // Load attachments separately
        let attachmentsData = [];
        try {
          const attachmentsResponse = await getProformaAttachments(id, invitationId);
          console.log('Attachments response:', attachmentsResponse); // Debug log
          attachmentsData = attachmentsResponse?.data || attachmentsResponse || [];
        } catch (attachErr) {
          console.warn('Failed to load attachments:', attachErr);
        }
        
        if (proformaResponse && proformaResponse.success && proformaResponse.data) {
          const proformaData = proformaResponse.data;
          
          const normalized = {
            proforma_number: proformaData.proforma_number || '',
            title: proformaData.title || '',
            client: proformaData.client || '',
            client_address: proformaData.client_address || '',
            client_email: proformaData.client_email || '',
            client_phone: proformaData.client_phone || '',
            date_issued: proformaData.date_issued
              ? new Date(proformaData.date_issued).toISOString().slice(0, 10)
              : '',
            expiry_date: proformaData.expiry_date
              ? new Date(proformaData.expiry_date).toISOString().slice(0, 10)
              : '',
            status: proformaData.status || 'draft',
            terms_and_conditions: proformaData.terms_and_conditions || '',
            notes: proformaData.notes || '',
            items: proformaData.items && proformaData.items.length > 0 
              ? proformaData.items.map(item => ({
                  description: item.description || '',
                  quantity: item.quantity || 1,
                  unit_price: item.unit_price || 0,
                  tax_rate: item.tax_rate || 0,
                  discount_amount: item.discount_amount || 0
                }))
              : [{ ...emptyItem }],
          };
          
          console.log('Normalized proforma data:', normalized); // Debug log
          setForm(normalized);
          setExistingAttachments(Array.isArray(attachmentsData) ? attachmentsData : []);
        } else {
          console.error('Invalid proforma response:', proformaResponse);
          setInitialError(proformaResponse?.error || 'No proforma data found');
        }
      } catch (err) {
        console.error('Failed to load proforma:', err);
        setInitialError(err.message || 'Failed to load proforma data');
      } finally {
        setInitialLoading(false);
      }
    };
    
    if (id) {
      loadData();
    }
  }, [id, invitationId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleItemChange = (idx, e) => {
    const items = form.items.map((item, i) =>
      i === idx ? { ...item, [e.target.name]: e.target.value } : item
    );
    setForm({ ...form, items });
  };

  const addItem = () => setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      setDeletingAttachments(prev => [...prev, attachmentId]);
      await deleteProformaAttachment(id, attachmentId, invitationId);
      setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingAttachments(prev => prev.filter(delId => delId !== attachmentId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation for required fields
    const requiredFields = ['title', 'client', 'date_issued', 'expiry_date'];
    const missingFields = requiredFields.filter(field => {
      const value = form[field];
      return !value || (typeof value === 'string' && value.trim() === '');
    });
    
    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    try {
      await saveProforma(id, form);
      
      // Handle new attachments if any
      if (files.length > 0) {
        setUploadingAttachments(true);
        await Promise.all(
          files.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', file.name);
            return uploadProformaAttachment(id, formData, invitationId);
          })
        );
        setUploadingAttachments(false);
        setFiles([]); // Clear files after upload
        // Reset file input
        const fileInput = document.getElementById('file-upload');
        if (fileInput) fileInput.value = '';
        
        // Refresh attachments after upload
        const attachments = await getProformaAttachments(id, invitationId);
        setExistingAttachments(attachments?.data || []);
      }
      
      setSuccessMessage('Proforma updated successfully!');
      setTimeout(() => {
        navigate(`/dashboard/collaborator/${invitationId}/proformas`);
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  if (initialError) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg">
        <h3 className="font-medium">Error loading proforma</h3>
        <p className="text-sm">{initialError}</p>
        <button 
          onClick={() => navigate(`/dashboard/collaborator/${invitationId}/proformas`)}
          className="mt-3 text-sm text-primary-600 hover:text-primary-800"
        >
          Return to proformas list
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate(`/dashboard/collaborator/${invitationId}/proformas`)}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Proformas
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Edit Proforma</h1>
      </div>

      {/* Display Messages */}
      <Message type="error" message={error} />
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Proforma Number</label>
              <input 
                name="proforma_number" 
                value={form.proforma_number} 
                onChange={handleChange} 
                placeholder="Proforma Number" 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input 
                name="title" 
                value={form.title} 
                onChange={handleChange} 
                placeholder="Title" 
                className="w-full border border-red-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500" 
                required 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
              <input 
                name="client" 
                value={form.client} 
                onChange={handleChange} 
                placeholder="Client Name" 
                className="w-full border border-red-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500" 
                required 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Address</label>
              <input 
                name="client_address" 
                value={form.client_address} 
                onChange={handleChange} 
                placeholder="Client Address" 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Email</label>
              <input 
                name="client_email" 
                value={form.client_email} 
                onChange={handleChange} 
                placeholder="Client Email" 
                type="email"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Phone</label>
              <input 
                name="client_phone" 
                value={form.client_phone} 
                onChange={handleChange} 
                placeholder="Client Phone" 
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-red-600 mb-1">Date Issued *</label>
              <input 
                name="date_issued" 
                type="date" 
                value={form.date_issued} 
                onChange={handleChange} 
                className="w-full border border-red-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-red-600 mb-1">Expiry Date *</label>
              <input 
                name="expiry_date" 
                type="date" 
                value={form.expiry_date} 
                onChange={handleChange} 
                className="w-full border border-red-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                required
              />
            </div>
          </div>

          {/* Terms and Notes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Terms and Conditions</label>
              <textarea 
                name="terms_and_conditions" 
                value={form.terms_and_conditions} 
                onChange={handleChange} 
                placeholder="Terms and Conditions" 
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea 
                name="notes" 
                value={form.notes} 
                onChange={handleChange} 
                placeholder="Notes" 
                rows="4"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select 
              name="status" 
              value={form.status} 
              onChange={handleChange} 
              className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {STATUS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Items */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Items</label>
            <div className="space-y-3">
              {form.items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-2 md:grid-cols-6 gap-2 p-3 bg-gray-50 rounded-lg">
                  <input 
                    name="description" 
                    value={item.description} 
                    onChange={e => handleItemChange(idx, e)} 
                    placeholder="Description" 
                    className="col-span-2 border border-gray-300 rounded px-2 py-1 text-sm" 
                  />
                  <input 
                    name="quantity" 
                    type="number" 
                    value={item.quantity} 
                    onChange={e => handleItemChange(idx, e)} 
                    placeholder="Qty" 
                    className="border border-gray-300 rounded px-2 py-1 text-sm" 
                  />
                  <input 
                    name="unit_price" 
                    type="number" 
                    step="0.01"
                    value={item.unit_price} 
                    onChange={e => handleItemChange(idx, e)} 
                    placeholder="Unit Price" 
                    className="border border-gray-300 rounded px-2 py-1 text-sm" 
                  />
                  <input 
                    name="tax_rate" 
                    type="number" 
                    step="0.01"
                    value={item.tax_rate} 
                    onChange={e => handleItemChange(idx, e)} 
                    placeholder="Tax %" 
                    className="border border-gray-300 rounded px-2 py-1 text-sm" 
                  />
                  <div className="flex gap-1">
                    <input 
                      name="discount_amount" 
                      type="number" 
                      step="0.01"
                      value={item.discount_amount} 
                      onChange={e => handleItemChange(idx, e)} 
                      placeholder="Discount" 
                      className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" 
                    />
                    <button 
                      type="button" 
                      className="text-red-600 hover:text-red-800 px-2" 
                      onClick={() => removeItem(idx)} 
                      disabled={form.items.length === 1}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
              <button 
                type="button" 
                className="bg-gray-200 hover:bg-gray-300 px-4 py-2 rounded text-sm" 
                onClick={addItem}
              >
                Add Item
              </button>
            </div>
          </div>

          {/* Existing Attachments */}
          {existingAttachments.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Attachments</label>
              <ul className="space-y-2">
                {existingAttachments.map(attachment => (
                  <li key={attachment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
                    <div className="flex items-center">
                      <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                      <span>{attachment.title || attachment.file_name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteAttachment(attachment.id)}
                      className="text-red-600 hover:text-red-800"
                      disabled={deletingAttachments.includes(attachment.id)}
                    >
                      {deletingAttachments.includes(attachment.id) ? (
                        'Deleting...'
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* New Attachments */}
          <div>
            <label htmlFor="attachments" className="block text-sm font-medium text-gray-700 mb-2">
              Add New Attachments (PDF, Images, etc.)
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
              <div className="space-y-1 text-center">
                <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                  >
                    <span>Upload files</span>
                    <input
                      id="file-upload"
                      name="file-upload"
                      type="file"
                      className="sr-only"
                      multiple
                      onChange={handleFileChange}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">
                  PDF, JPG, PNG up to 10MB
                </p>
              </div>
            </div>
            {files.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-500">Files to upload:</p>
                <ul className="mt-1 text-sm text-gray-700">
                  {Array.from(files).map((file, index) => (
                    <li key={index} className="flex items-center">
                      <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                      {file.name} ({Math.round(file.size / 1024)} KB)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/collaborator/${invitationId}/proformas`)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50" 
              disabled={loading || uploadingAttachments}
            >
              {uploadingAttachments ? 'Updating...' : 'Update Proforma'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditInviterProformaPage;